/**
 * TransactionsPage — Transaction list with bucket color coding + merchant review queue
 *
 * Tabs: All | Needs | Wants | Savings | Income | Review (N)
 * Review mode: groups needs_review transactions by merchant (~40-60 decisions vs 1574),
 * one confirm categorizes all transactions from that merchant + saves a persistent rule.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getTransactionsByMonth, getCategoryOverrides, getReviewQueueCount,
  getMerchantReviewQueue, categorizeMerchant, applyExistingRules,
} from '../services/db';
import { getCategoryBucket } from '../utils/budget';
import { useCategoryContext } from '../contexts/CategoryContext';
import { formatCurrency, formatDate, getMonthKey, shiftMonthKey, formatMonthLabel } from '../utils/format';
import { SkeletonLine } from '../components/Skeleton';
import theme from '../utils/theme';

// ============ CATEGORY PICKER MODAL ============

function CategoryPickerModal({ merchant, taxonomy, bucketNames, onSelect, onClose }) {
  const [expanded, setExpanded] = useState(false);

  // Find the suggested group so we can show it first
  const suggestedGroup = merchant?.suggestedCategory
    ? taxonomy.find(g => g.categories.some(c => c.label === merchant.suggestedCategory))
    : null;

  // In default view: show suggested group's categories + "More categories..."
  // In expanded view: show all groups
  const groups = expanded
    ? taxonomy
    : suggestedGroup
      ? [suggestedGroup]
      : taxonomy.slice(0, 2);

  const bucketColor = { need: theme.bucketNeed, want: theme.bucketWant, savings: theme.bucketSavings, income: theme.bucketIncome };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Choose Category</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: theme.textMuted }}>×</button>
        </div>

        {groups.map(group => (
          <div key={group.group} className="cat-picker-group">
            <div className="cat-picker-group-label">{group.group}</div>
            {group.categories.map(cat => {
              const isSelected = merchant?.suggestedCategory === cat.label;
              return (
                <button
                  key={cat.label}
                  className={`cat-picker-row${isSelected ? ' cat-picker-row--selected' : ''}`}
                  onClick={() => onSelect(cat.label, cat.bucket)}
                >
                  <span className="cat-picker-label">{cat.label}</span>
                  <span
                    className={`cat-picker-bucket cat-picker-bucket--${cat.bucket}`}
                    style={{ background: bucketColor[cat.bucket] + '20', color: bucketColor[cat.bucket] }}
                  >
                    {bucketNames?.[cat.bucket] || cat.bucket}
                  </span>
                </button>
              );
            })}
          </div>
        ))}

        {!expanded && (
          <button
            onClick={() => setExpanded(true)}
            style={{
              width: '100%', padding: '0.6rem', marginTop: '0.5rem',
              background: 'none', border: `1px solid ${theme.border}`, borderRadius: '8px',
              fontSize: '0.85rem', color: theme.textSecondary, cursor: 'pointer',
            }}
          >
            More categories...
          </button>
        )}
      </div>
    </div>
  );
}

// ============ MERCHANT CARD ============

function MerchantCard({ merchant, bucketNames, onConfirm, onSkip, onOpenPicker }) {
  const { merchantKey, sampleDescription, count, totalAmount, dateRange, suggestedCategory, suggestedBucket } = merchant;
  const hasCategory = !!suggestedCategory;
  const bucketColor = { need: theme.bucketNeed, want: theme.bucketWant, savings: theme.bucketSavings, income: theme.bucketIncome };

  return (
    <div className="merchant-card">
      <div className="merchant-card-name">{sampleDescription}</div>
      <div className="merchant-card-meta">
        {count} transaction{count !== 1 ? 's' : ''} · {formatCurrency(totalAmount)}
        {dateRange ? ` · ${dateRange}` : ''}
      </div>

      <button className="merchant-cat-selector" onClick={() => onOpenPicker(merchant)}>
        {hasCategory ? (
          <>
            <span className="merchant-cat-label">{suggestedCategory}</span>
            <span
              className={`merchant-cat-bucket merchant-cat-bucket--${suggestedBucket}`}
              style={{ background: bucketColor[suggestedBucket] + '20', color: bucketColor[suggestedBucket] }}
            >
              {bucketNames?.[suggestedBucket] || suggestedBucket}
            </span>
          </>
        ) : (
          <span style={{ color: theme.textMuted }}>Tap to choose category</span>
        )}
        <span style={{ marginLeft: 'auto', color: theme.textMuted }}>›</span>
      </button>

      <div className="merchant-card-actions">
        <button
          className="btn btn-primary"
          onClick={() => onConfirm(merchant, suggestedCategory, suggestedBucket)}
          disabled={!hasCategory}
          style={{ flex: 1 }}
        >
          Confirm
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => onSkip(merchantKey)}
          style={{ flex: 1 }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============

export default function TransactionsPage() {
  const { user } = useAuth();
  const { effectiveTaxonomy, bucketNames } = useCategoryContext();
  const [monthKey, setMonthKey] = useState(getMonthKey());
  const [transactions, setTransactions] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [bucketFilter, setBucketFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  // Review queue state
  const [merchantQueue, setMerchantQueue] = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [autoApplied, setAutoApplied] = useState(0);
  const [catPickerOpen, setCatPickerOpen] = useState(false);
  const [activeMerchant, setActiveMerchant] = useState(null);
  const [reviewPhase, setReviewPhase] = useState('idle'); // idle | loading | ready

  const isReview = bucketFilter === 'review';

  // Loads monthly transactions + review badge count
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [txns, cats, count] = await Promise.all([
        getTransactionsByMonth(user.id, monthKey),
        getCategoryOverrides(user.id),
        getReviewQueueCount(user.id),
      ]);
      setTransactions(txns);
      setOverrides(cats);
      setReviewCount(count);
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [user.id, monthKey]);

  useEffect(() => { loadData(); }, [loadData]);

  // Loads merchant review queue — applies existing rules first, then fetches remaining
  const loadReviewData = useCallback(async () => {
    setReviewPhase('loading');
    try {
      const applied = await applyExistingRules(user.id);
      const [queue, count] = await Promise.all([
        getMerchantReviewQueue(user.id),
        getReviewQueueCount(user.id),
      ]);
      setAutoApplied(applied);
      setMerchantQueue(queue);
      setTotalMerchants(queue.length);
      setConfirmedCount(0);
      setReviewCount(count);
      setReviewPhase('ready');
    } catch (err) {
      setError(err.message || 'Failed to load review queue');
      setReviewPhase('idle');
    }
  }, [user.id]);

  // Reload review data whenever entering the review tab
  useEffect(() => {
    if (isReview) {
      loadReviewData();
    } else {
      setReviewPhase('idle');
    }
  }, [isReview]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmMerchant = async (merchant, categoryLabel, bucket) => {
    // Optimistic update — remove from queue immediately for snappy feel
    setMerchantQueue(q => q.filter(m => m.merchantKey !== merchant.merchantKey));
    setConfirmedCount(c => c + 1);
    setReviewCount(c => Math.max(0, c - merchant.count));
    try {
      await categorizeMerchant(user.id, merchant.merchantKey, categoryLabel, bucket, merchant.transactionIds);
    } catch (err) {
      console.error('Categorize merchant failed:', err);
      loadReviewData(); // Restore accurate state on error
    }
  };

  const handleCategorySelect = (categoryLabel, bucket) => {
    if (activeMerchant) {
      setMerchantQueue(q => q.map(m =>
        m.merchantKey === activeMerchant.merchantKey
          ? { ...m, suggestedCategory: categoryLabel, suggestedBucket: bucket }
          : m
      ));
    }
    setCatPickerOpen(false);
    setActiveMerchant(null);
  };

  const handleSkipMerchant = (merchantKey) => {
    // Local-only removal — merchant reappears on next load (skip = "not now")
    setMerchantQueue(q => q.filter(m => m.merchantKey !== merchantKey));
  };

  const handleOpenPicker = (merchant) => {
    setActiveMerchant(merchant);
    setCatPickerOpen(true);
  };

  // Normal tab filter logic
  const filtered = useMemo(() => {
    if (isReview) return [];
    return transactions.filter(txn => {
      if (bucketFilter !== 'all') {
        const bucket = txn.bucket || getCategoryBucket(txn.category, overrides);
        if (bucket !== bucketFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!(txn.description || '').toLowerCase().includes(q) &&
            !(txn.category || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [transactions, bucketFilter, search, overrides, isReview]);

  const totalSpent = filtered
    .filter(t => !t.is_credit)
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const filters = [
    { key: 'all',     label: 'All' },
    { key: 'need',    label: 'Needs' },
    { key: 'want',    label: 'Wants' },
    { key: 'savings', label: 'Savings' },
    { key: 'income',  label: 'Income' },
    { key: 'review',  label: reviewCount > 0 ? `Review (${reviewCount})` : 'Review', badge: reviewCount > 0 },
  ];

  const progressPct = totalMerchants > 0 ? Math.round((confirmedCount / totalMerchants) * 100) : 0;

  return (
    <div>
      {/* Month selector — hidden in review mode (queue is cross-month) */}
      {!isReview && (
        <div className="month-selector">
          <button onClick={() => setMonthKey(k => shiftMonthKey(k, -1))}>‹</button>
          <span className="month-label">{formatMonthLabel(monthKey)}</span>
          <button onClick={() => setMonthKey(k => shiftMonthKey(k, 1))}>›</button>
        </div>
      )}

      {/* Search — hidden in review mode */}
      {!isReview && (
        <input
          className="search-bar"
          type="text"
          placeholder="Search transactions..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      )}

      {/* Filter tabs */}
      <div className="filter-tabs">
        {filters.map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${bucketFilter === f.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setBucketFilter(f.key)}
            style={f.badge ? { position: 'relative' } : {}}
          >
            {f.label}
            {f.badge && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                background: theme.danger, color: 'white',
                borderRadius: '50%', width: '16px', height: '16px',
                fontSize: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700,
              }}>
                {reviewCount > 99 ? '99+' : reviewCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Summary bar — normal mode only */}
      {!isReview && (
        <div className="summary-bar">
          <span>{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</span>
          <span>{formatCurrency(totalSpent)} spent</span>
        </div>
      )}

      {error && (
        <div className="card">
          <div className="alert alert-error">{error}</div>
          <button className="btn btn-primary" onClick={() => { setError(null); loadData(); }}>Retry</button>
        </div>
      )}

      {/* ===== REVIEW MODE ===== */}
      {isReview ? (
        <div>
          {/* Loading skeleton */}
          {reviewPhase === 'loading' && (
            <div className="card" style={{ textAlign: 'center', padding: '1.5rem', color: theme.textMuted, fontSize: '0.9rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>Checking existing rules...</div>
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton-row">
                  <SkeletonLine className="skeleton-line--medium" />
                  <SkeletonLine className="skeleton-line--short" />
                </div>
              ))}
            </div>
          )}

          {/* All done celebration */}
          {reviewPhase === 'ready' && merchantQueue.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '2rem 1rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.text, marginBottom: '0.35rem' }}>
                All done!
              </div>
              <div style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
                {confirmedCount > 0 && <span>{confirmedCount} merchant{confirmedCount !== 1 ? 's' : ''} confirmed</span>}
                {confirmedCount > 0 && autoApplied > 0 && <span> · </span>}
                {autoApplied > 0 && <span>{autoApplied} transaction{autoApplied !== 1 ? 's' : ''} auto-applied</span>}
              </div>
            </div>
          )}

          {/* Merchant queue */}
          {reviewPhase === 'ready' && merchantQueue.length > 0 && (
            <>
              {/* Progress header */}
              <div className="merchant-progress-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text }}>
                    {merchantQueue.length} merchant{merchantQueue.length !== 1 ? 's' : ''} to review
                  </span>
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>
                    {progressPct}%
                  </span>
                </div>
                <div className="merchant-progress-bar">
                  <div
                    className="merchant-progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {autoApplied > 0 && (
                  <div style={{ fontSize: '0.75rem', color: theme.textMuted, marginTop: '0.4rem' }}>
                    {autoApplied} transaction{autoApplied !== 1 ? 's' : ''} auto-applied from saved rules
                  </div>
                )}
              </div>

              {/* Merchant cards */}
              {merchantQueue.map(merchant => (
                <MerchantCard
                  key={merchant.merchantKey}
                  merchant={merchant}
                  bucketNames={bucketNames}
                  onConfirm={handleConfirmMerchant}
                  onSkip={handleSkipMerchant}
                  onOpenPicker={handleOpenPicker}
                />
              ))}
            </>
          )}
        </div>
      ) : (
        /* ===== NORMAL MODE ===== */
        <div className="card">
          {loading ? (
            [1, 2, 3, 4, 5].map(i => (
              <div key={i} className="skeleton-row">
                <SkeletonLine className="skeleton-line--medium" />
                <SkeletonLine className="skeleton-line--short" />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <p className="empty-state">
              {transactions.length === 0
                ? 'No transactions this month. Tap Sync on the dashboard.'
                : 'No transactions match your filters.'}
            </p>
          ) : (
            filtered.map(txn => {
              const bucket = txn.bucket || getCategoryBucket(txn.category, overrides);
              return (
                <div key={txn.id} className={`txn-row ${bucket}`}>
                  <div className={`txn-dot ${bucket}`} />
                  <div className="txn-info">
                    <div className="txn-desc">
                      {txn.description || 'Unknown'}
                      {txn.is_bill_payment && <span className="txn-bill-badge">Bill</span>}
                      {txn.needs_review && <span className="txn-bill-badge" style={{ background: theme.warning }}>Review</span>}
                    </div>
                    <div className="txn-date">
                      {formatDate(txn.date)} · {txn.category || 'Uncategorized'}
                    </div>
                  </div>
                  <div className={`txn-amount ${txn.is_credit ? 'income' : 'expense'}`}>
                    {txn.is_credit ? '+' : ''}{formatCurrency(txn.amount)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Category picker modal */}
      {catPickerOpen && activeMerchant && (
        <CategoryPickerModal
          merchant={activeMerchant}
          taxonomy={effectiveTaxonomy}
          bucketNames={bucketNames}
          onSelect={handleCategorySelect}
          onClose={() => { setCatPickerOpen(false); setActiveMerchant(null); }}
        />
      )}
    </div>
  );
}
